/**
 * ComputerVisionTest.js — React component for MediaPipe pose-based clinical tests.
 *
 * Renders a camera feed, runs MediaPipe PoseLandmarker, tracks movement,
 * shows real-time angle feedback, and sends sampled landmarks to the backend
 * for authoritative validation when the test is complete.
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { PoseLandmarker, FaceLandmarker, HandLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { MovementTracker } from './movementTracker';
import {
  Button, Typography, Paper, LinearProgress, Box, CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { calculateTorsoInclinationSide, calculateSagittalHeadTorsoAngles, calculateNeckRotation, calculateNeckExtension, calculateNeckFlexionSide } from './angleUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    maxWidth: 640,
    margin: '0 auto',
  },
  videoContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: 480,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#000',
    marginBottom: theme.spacing(2),
  },
  video: {
    width: '100%',
    transform: 'scaleX(-1)', // mirror
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transform: 'scaleX(-1)',
    pointerEvents: 'none',
  },
  overlay: {
    position: 'absolute',
    top: 8,
    left: 8,
    right: 8,
    backgroundColor: 'rgba(0,0,0,0.55)',
    borderRadius: 8,
    padding: theme.spacing(1),
    color: '#fff',
  },
  phaseLabel: {
    fontSize: '0.9rem',
    textAlign: 'center',
    marginTop: 4,
  },
  compensationWarning: {
    color: '#ff9800',
    fontSize: '0.85rem',
    textAlign: 'center',
    marginTop: 4,
  },
  instructionBox: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  resultBox: {
    width: '100%',
    maxWidth: 480,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  btnRow: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
}));

// Phase display name mapping
function phaseDisplayName(name) {
  const map = {
    resting: 'Get ready…',
    neutral: 'Start in neutral',
    abduction_ascend: 'Raise your arm',
    abduction_descend: 'Lower your arm',
    flexion: 'Flex forward',
    extension: 'Extend backward',
    rotation_left: 'Turn left',
    rotation_right: 'Turn right',
    lateral_flexion_left: 'Tilt left',
    lateral_flexion_right: 'Tilt right',
    complete: 'Done!',
  };
  return map[name] || name;
}

/**
 * Evaluate Bakody's sign end-pose.
 * Checks both arms — returns the one meeting all criteria (if any).
 *
 * Criteria (from reference image — arm raised, hand flat on top of head):
 *   1. Shoulder abduction:  angle(hip→shoulder→elbow) > 120°
 *   2. Elbow flexion:       angle(shoulder→elbow→wrist) between 40° and 140°
 *   3. Wrist above nose:    wrist.y < nose.y  (lower Y = higher on screen in normalised coords)
 *   4. Hand near midline:   |wrist.x - nose.x| < 0.15
 *   5. (Optional) HandLandmarker: middle-finger tip near head level
 */
function evaluateBakodyPose(poseLandmarks, handLandmarksArray) {
  const nose = poseLandmarks?.[0];
  if (!nose) return { correct: false, activeArm: null, checks: {} };

  const armDefs = [
    { name: 'left',  shoulder: 11, elbow: 13, wrist: 15, hip: 23 },
    { name: 'right', shoulder: 12, elbow: 14, wrist: 16, hip: 24 },
  ];

  const angle3 = (a, b, c) => {
    const ba = { x: a.x - b.x, y: a.y - b.y };
    const bc = { x: c.x - b.x, y: c.y - b.y };
    const dot = ba.x * bc.x + ba.y * bc.y;
    const magBA = Math.sqrt(ba.x * ba.x + ba.y * ba.y);
    const magBC = Math.sqrt(bc.x * bc.x + bc.y * bc.y);
    if (magBA < 1e-8 || magBC < 1e-8) return 0;
    const cosA = Math.max(-1, Math.min(1, dot / (magBA * magBC)));
    return (Math.acos(cosA) * 180) / Math.PI;
  };

  for (const arm of armDefs) {
    const shoulder = poseLandmarks?.[arm.shoulder];
    const elbow    = poseLandmarks?.[arm.elbow];
    const wrist    = poseLandmarks?.[arm.wrist];
    const hip      = poseLandmarks?.[arm.hip];
    if (!shoulder || !elbow || !wrist || !hip) continue;

    const shoulderAbductionAngle = angle3(hip, shoulder, elbow);
    const elbowFlexionAngle      = angle3(shoulder, elbow, wrist);
    const wristAboveNose         = (wrist.y || 1) < (nose.y || 0);
    const handNearMidline        = Math.abs((wrist.x || 0) - (nose.x || 0)) < 0.15;

    const shoulderOk = shoulderAbductionAngle > 120;
    const elbowOk    = elbowFlexionAngle >= 40 && elbowFlexionAngle <= 140;

    // Optional hand-landmark refinement: middle-finger tip (idx 12) near head
    let handConfirmed = true; // true by default if HandLandmarker unavailable
    if (handLandmarksArray && handLandmarksArray.length > 0) {
      // Find the hand closest to the wrist
      let bestDist = Infinity;
      let bestHand = null;
      for (const hl of handLandmarksArray) {
        if (!hl || !hl[0]) continue;
        const d = Math.hypot((hl[0].x || 0) - (wrist.x || 0), (hl[0].y || 0) - (wrist.y || 0));
        if (d < bestDist) { bestDist = d; bestHand = hl; }
      }
      if (bestHand && bestHand[12]) {
        // Middle fingertip should be near or above nose level
        handConfirmed = (bestHand[12].y || 1) < ((nose.y || 0) + 0.06);
      }
    }

    const checks = {
      shoulderAbduction: shoulderOk,
      elbowFlexion: elbowOk,
      handAboveHead: wristAboveNose,
      handCentered: handNearMidline,
      handConfirmed,
    };

    if (shoulderOk && elbowOk && wristAboveNose && handNearMidline && handConfirmed) {
      return { correct: true, activeArm: arm.name, checks };
    }
  }

  return { correct: false, activeArm: null, checks: {} };
}

/**
 * Evaluate Vasalva pose — hand held in front of mouth.
 *
 * Criteria:
 *   1. Wrist or hand landmarks near mouth level
 *   2. Wrist or hand landmarks in front of the mouth
 *   3. Elbow below wrist:       elbow.y > wrist.y  (arm raised towards face)
 *   4. HandLandmarker may satisfy the mouth-contact check using fingertips
 *      or finger phalanges even when the pose wrist is slightly away.
 */
function evaluateVasalvaPose(poseLandmarks, handLandmarksArray) {
  const nose = poseLandmarks?.[0];
  // Mouth landmarks: 9 = mouth left corner, 10 = mouth right corner
  const mouthLeft = poseLandmarks?.[9];
  const mouthRight = poseLandmarks?.[10];
  if (!nose || !mouthLeft || !mouthRight) return { correct: false, activeArm: null, checks: {} };

  const mouthY = ((mouthLeft.y || 0) + (mouthRight.y || 0)) / 2;
  const mouthX = ((mouthLeft.x || 0) + (mouthRight.x || 0)) / 2;

  const armDefs = [
    { name: 'left',  elbow: 13, wrist: 15 },
    { name: 'right', elbow: 14, wrist: 16 },
  ];

  for (const arm of armDefs) {
    const elbow = poseLandmarks?.[arm.elbow];
    const wrist = poseLandmarks?.[arm.wrist];
    if (!elbow || !wrist) continue;

    const wristNearMouth = Math.abs((wrist.y || 0) - mouthY) < 0.08;
    const wristInFront   = Math.abs((wrist.x || 0) - mouthX) < 0.18;
    const elbowBelowWrist = (elbow.y || 0) > (wrist.y || 0);
    const wristContact = wristNearMouth && wristInFront;

    // Optional hand-landmark refinement: allow fingertips or finger joints
    // near the mouth to satisfy the contact check.
    let handConfirmed = true;
    let matchedHandFound = false;
    let handNearMouth = false;
    let handInFront = false;
    if (handLandmarksArray && handLandmarksArray.length > 0) {
      const bestHand = findHandForWrist(handLandmarksArray, wrist);
      if (bestHand) {
        matchedHandFound = true;
        const mouthContactPoint = bestHand.find((point) => point
          && Math.abs((point.y || 0) - mouthY) < 0.12
          && Math.abs((point.x || 0) - mouthX) < 0.20);
        if (mouthContactPoint) {
          handNearMouth = true;
          handInFront = true;
        }
      }
    }

    const handContact = handNearMouth && handInFront;
    const mouthContact = wristContact || handContact;
    handConfirmed = !matchedHandFound || handContact || wristContact;

    const checks = {
      wristNearMouth,
      wristInFront,
      handNearMouth,
      handInFront,
      mouthContact,
      elbowBelowWrist,
      handConfirmed,
    };

    if (mouthContact && elbowBelowWrist && handConfirmed) {
      return { correct: true, activeArm: arm.name, checks };
    }
  }

  return { correct: false, activeArm: null, checks: {} };
}

/**
 * Helper: find the hand landmark set closest to a given pose wrist.
 */
function findHandForWrist(handLandmarksArray, wrist) {
  if (!handLandmarksArray || handLandmarksArray.length === 0 || !wrist) return null;
  let bestDist = Infinity;
  let bestHand = null;
  for (const hl of handLandmarksArray) {
    if (!hl || !hl[0]) continue;
    const d = Math.hypot((hl[0].x || 0) - (wrist.x || 0), (hl[0].y || 0) - (wrist.y || 0));
    if (d < bestDist) { bestDist = d; bestHand = hl; }
  }
  return bestHand;
}

/**
 * Helper: check wrist extension via hand landmarks.
 * Wrist extension = hand bent back (dorsiflexion).
 * We measure the angle  elbow → poseWrist → middleFingerMCP.
 * An extended wrist produces a smaller angle (< ~160°) because the hand
 * bends away from the forearm line.  We also accept that the MCP (hand
 * landmark 9) is higher (lower y) than the wrist when the arm is roughly
 * horizontal, which is a simpler but robust proxy.
 */
function isWristExtended(hand, poseWrist, poseElbow) {
  if (!hand || !poseWrist || !poseElbow) return false;
  const mcp = hand[9]; // middle-finger MCP
  if (!mcp) return false;

  // For a roughly-horizontal forearm the simplest cue: MCP is above wrist
  // (lower normalised y) when the wrist is dorsiflexed.
  const mcpAboveWrist = (mcp.y || 0) < (poseWrist.y || 0) - 0.01;

  // Secondary: angular check. Angle  elbow→wrist→mcp  < 160° indicates extension.
  const angle3 = (a, b, c) => {
    const ba = { x: a.x - b.x, y: a.y - b.y };
    const bc = { x: c.x - b.x, y: c.y - b.y };
    const dot = ba.x * bc.x + ba.y * bc.y;
    const magBA = Math.sqrt(ba.x * ba.x + ba.y * ba.y);
    const magBC = Math.sqrt(bc.x * bc.x + bc.y * bc.y);
    if (magBA < 1e-8 || magBC < 1e-8) return 180;
    const cosA = Math.max(-1, Math.min(1, dot / (magBA * magBC)));
    return (Math.acos(cosA) * 180) / Math.PI;
  };
  const wristAngle = angle3(poseElbow, poseWrist, mcp);
  const angleOk = wristAngle < 160;

  return mcpAboveWrist || angleOk;
}

const angle3Util = (a, b, c) => {
  const ba = { x: a.x - b.x, y: a.y - b.y };
  const bc = { x: c.x - b.x, y: c.y - b.y };
  const dot = ba.x * bc.x + ba.y * bc.y;
  const magBA = Math.sqrt(ba.x * ba.x + ba.y * ba.y);
  const magBC = Math.sqrt(bc.x * bc.x + bc.y * bc.y);
  if (magBA < 1e-8 || magBC < 1e-8) return 0;
  const cosA = Math.max(-1, Math.min(1, dot / (magBA * magBC)));
  return (Math.acos(cosA) * 180) / Math.PI;
};

/**
 * ULNTT N. Medianus pose evaluation.
 *
 * Criteria (symptomatic side — check both arms, accept either):
 *   1. Shoulder abduction ~90°  (angle hip→shoulder→elbow  70–110°)
 *   2. Wrist extended (hand landmarks)
 *   3. Neck side-bent away from tested arm:
 *      nose.x shifted towards the opposite side of the tested arm
 */
function evaluateUlnttMedianusPose(poseLandmarks, handLandmarksArray) {
  const nose = poseLandmarks?.[0];
  if (!nose) return { correct: false, activeArm: null, checks: {} };

  const armDefs = [
    { name: 'left',  shoulder: 11, elbow: 13, wrist: 15, hip: 23 },
    { name: 'right', shoulder: 12, elbow: 14, wrist: 16, hip: 24 },
  ];

  for (const arm of armDefs) {
    const shoulder = poseLandmarks?.[arm.shoulder];
    const elbow    = poseLandmarks?.[arm.elbow];
    const wrist    = poseLandmarks?.[arm.wrist];
    const hip      = poseLandmarks?.[arm.hip];
    if (!shoulder || !elbow || !wrist || !hip) continue;

    const shoulderAngle = angle3Util(hip, shoulder, elbow);
    const shoulderOk = shoulderAngle >= 70 && shoulderAngle <= 110;

    // Wrist extension via hand landmarks
    const hand = findHandForWrist(handLandmarksArray, wrist);
    const wristOk = isWristExtended(hand, wrist, elbow);

    // Neck side-bent away: nose should be shifted toward the opposite side.
    // For left arm tested → nose should move rightward (higher x in mirrored view,
    // but in normalised coords, right = higher x).
    // We check that nose.x is shifted away from the tested shoulder.
    const shoulderMidX = ((poseLandmarks?.[11]?.x || 0) + (poseLandmarks?.[12]?.x || 0)) / 2;
    const noseOffsetFromMid = (nose.x || 0) - shoulderMidX;
    // Left arm: nose should shift right (positive offset)
    // Right arm: nose should shift left (negative offset)
    const neckSideBent = arm.name === 'left'
      ? noseOffsetFromMid > 0.02
      : noseOffsetFromMid < -0.02;

    const checks = {
      shoulderAbduction: shoulderOk,
      wristExtension: wristOk,
      neckSideBent,
    };

    if (shoulderOk && wristOk && neckSideBent) {
      return { correct: true, activeArm: arm.name, checks };
    }
  }

  return { correct: false, activeArm: null, checks: {} };
}

/**
 * ULNTT N. Radialis pose evaluation.
 *
 * Criteria:
 *   1. Head neutral (upright, looking straight ahead) — nose centred over shoulders
 *   2. Arm fairly straight: elbow angle > 140°
 *   3. Slight shoulder abduction: angle(hip→shoulder→elbow) 15–60°
 *   4. Wrist extended (hand landmarks)
 *   5. Fingers near hip Y level: hand fingertip y ≈ same-side hip y
 *   6. Pinky closer to body than thumb (hand orientation)
 */
function evaluateUlnttRadialisPose(poseLandmarks, handLandmarksArray) {
  const nose = poseLandmarks?.[0];
  if (!nose) return { correct: false, activeArm: null, checks: {} };

  // Head neutral: nose centred
  const lShoulder = poseLandmarks?.[11];
  const rShoulder = poseLandmarks?.[12];
  if (!lShoulder || !rShoulder) return { correct: false, activeArm: null, checks: {} };
  const shoulderMidX = ((lShoulder.x || 0) + (rShoulder.x || 0)) / 2;
  const headNeutral = Math.abs((nose.x || 0) - shoulderMidX) < 0.06;

  const armDefs = [
    { name: 'left',  shoulder: 11, elbow: 13, wrist: 15, hip: 23, pinkyIdx: 20, thumbIdx: 4 },
    { name: 'right', shoulder: 12, elbow: 14, wrist: 16, hip: 24, pinkyIdx: 20, thumbIdx: 4 },
  ];

  for (const arm of armDefs) {
    const shoulder = poseLandmarks?.[arm.shoulder];
    const elbow    = poseLandmarks?.[arm.elbow];
    const wrist    = poseLandmarks?.[arm.wrist];
    const hip      = poseLandmarks?.[arm.hip];
    if (!shoulder || !elbow || !wrist || !hip) continue;

    const elbowAngle = angle3Util(shoulder, elbow, wrist);
    const elbowOk = elbowAngle > 140; // arm fairly straight

    const shoulderAngle = angle3Util(hip, shoulder, elbow);
    const shoulderOk = shoulderAngle >= 15 && shoulderAngle <= 60;

    // Wrist extension via hand landmarks
    const hand = findHandForWrist(handLandmarksArray, wrist);
    const wristOk = isWristExtended(hand, wrist, elbow);

    // Fingers near hip Y level
    let fingersNearHip = false;
    if (hand && hand[12]) { // middle finger tip
      fingersNearHip = Math.abs((hand[12].y || 0) - (hip.y || 0)) < 0.12;
    } else {
      // Fallback to pose wrist
      fingersNearHip = Math.abs((wrist.y || 0) - (hip.y || 0)) < 0.12;
    }

    // Pinky closer to body than thumb.
    // "Closer to body" = closer to shoulder midline (shoulderMidX).
    let pinkyCloser = false;
    if (hand && hand[20] && hand[4]) {
      const pinkyX = hand[20].x || 0;
      const thumbX = hand[4].x || 0;
      const pinkyDistToMid = Math.abs(pinkyX - shoulderMidX);
      const thumbDistToMid = Math.abs(thumbX - shoulderMidX);
      pinkyCloser = pinkyDistToMid < thumbDistToMid;
    } else {
      pinkyCloser = true; // no hand data — skip this check
    }

    const checks = {
      headNeutral,
      armStraight: elbowOk,
      shoulderAbduction: shoulderOk,
      wristExtension: wristOk,
      fingersNearHip,
      pinkyCloser,
    };

    if (headNeutral && elbowOk && shoulderOk && wristOk && fingersNearHip && pinkyCloser) {
      return { correct: true, activeArm: arm.name, checks };
    }
  }

  return { correct: false, activeArm: null, checks: {} };
}

/**
 * ULNTT N. Ulnaris pose evaluation.
 *
 * Criteria:
 *   1. Head neutral (upright, looking straight ahead) — nose centred over shoulders
 *   2. Shoulder abduction ≥ 80°: angle(hip→shoulder→elbow)
 *   3. Elbow flexion ≥ 90°:     angle(shoulder→elbow→wrist) ≤ 100° (bent)
 *   4. Wrist extended (hand landmarks)
 *   5. Fingers close below ear Y coordinate
 */
function evaluateUlnttUlnarisPose(poseLandmarks, handLandmarksArray) {
  const nose = poseLandmarks?.[0];
  if (!nose) return { correct: false, activeArm: null, checks: {} };

  // Head neutral
  const lShoulder = poseLandmarks?.[11];
  const rShoulder = poseLandmarks?.[12];
  if (!lShoulder || !rShoulder) return { correct: false, activeArm: null, checks: {} };
  const shoulderMidX = ((lShoulder.x || 0) + (rShoulder.x || 0)) / 2;
  const headNeutral = Math.abs((nose.x || 0) - shoulderMidX) < 0.06;

  const armDefs = [
    { name: 'left',  shoulder: 11, elbow: 13, wrist: 15, hip: 23, ear: 7 },
    { name: 'right', shoulder: 12, elbow: 14, wrist: 16, hip: 24, ear: 8 },
  ];

  for (const arm of armDefs) {
    const shoulder = poseLandmarks?.[arm.shoulder];
    const elbow    = poseLandmarks?.[arm.elbow];
    const wrist    = poseLandmarks?.[arm.wrist];
    const hip      = poseLandmarks?.[arm.hip];
    const ear      = poseLandmarks?.[arm.ear];
    if (!shoulder || !elbow || !wrist || !hip) continue;

    const shoulderAngle = angle3Util(hip, shoulder, elbow);
    const shoulderOk = shoulderAngle >= 80;

    // Elbow flexion: the interior angle at elbow. ≤100° means bent ≥80°.
    const elbowAngle = angle3Util(shoulder, elbow, wrist);
    const elbowOk = elbowAngle <= 100;

    // Wrist extension via hand landmarks
    const hand = findHandForWrist(handLandmarksArray, wrist);
    const wristOk = isWristExtended(hand, wrist, elbow);

    // Fingers below ear Y (close to it).
    // Use hand middle-finger tip or pose wrist as proxy.
    let fingersNearEar = false;
    const earY = ear ? (ear.y || 0) : (nose.y || 0);
    if (hand && hand[12]) {
      const fingerY = hand[12].y || 0;
      // Finger should be near or below ear: within 0.15 below ear
      fingersNearEar = fingerY >= earY && fingerY <= earY + 0.15;
    } else {
      fingersNearEar = (wrist.y || 0) >= earY && (wrist.y || 0) <= earY + 0.18;
    }

    const checks = {
      headNeutral,
      shoulderAbduction: shoulderOk,
      elbowFlexion: elbowOk,
      wristExtension: wristOk,
      fingersNearEar,
    };

    if (headNeutral && shoulderOk && elbowOk && wristOk && fingersNearEar) {
      return { correct: true, activeArm: arm.name, checks };
    }
  }

  return { correct: false, activeArm: null, checks: {} };
}

/**
 * Module-level state for Spurling's test baseline tracking.
 * Reset when the staged posture sequence is lost.
 */
const spurlingsState = {
  phase: 'spurling_rotation',
  activeSide: null,
  baselineNoseY: null,
  rotationFrames: 0,
  tiltFrames: 0,
  extensionFrames: 0,
};

const SPURLINGS_ROTATION_THRESHOLD_DEG = 30; // minimum neck rotation to count as deliberate rotation
const SPURLINGS_SIDEBEND_THRESHOLD = 0.015; // minimum ear Y delta to count as side-bend (in normalised coords, ~1.7% of screen height) — tuned for sensitivity without false positives from natural head sway  
const SPURLINGS_EXTENSION_THRESHOLD = 0.015; // minimum nose Y delta to count as extension (in normalised coords, ~1.7% of screen height) — tuned for sensitivity without false positives from natural head sway  

function resetSpurlingsState() {
  spurlingsState.phase = 'spurling_rotation';
  spurlingsState.activeSide = null;
  spurlingsState.baselineNoseY = null;
  spurlingsState.rotationFrames = 0;
  spurlingsState.tiltFrames = 0;
  spurlingsState.extensionFrames = 0;
}

/**
 * Spurling's Test pose evaluation (staged detection).
 *
 * Phase 1 — Rotate toward the painful side.
 * Phase 2 — Keep rotated and side-bend opposite that side.
 * Phase 3 — Keep both and tilt the head backward.
 *
 * Each phase must be recognised before the next instruction is shown.
 */
function evaluateSpurlingsPose(poseLandmarks /*, handLandmarksArray — unused */) {
  const nose = poseLandmarks?.[0];
  const lEar = poseLandmarks?.[7];   // patient's left ear
  const rEar = poseLandmarks?.[8];   // patient's right ear
  const lShoulder = poseLandmarks?.[11];
  const rShoulder = poseLandmarks?.[12];

  if (!nose || !lEar || !rEar || !lShoulder || !rShoulder) {
    resetSpurlingsState();
    return { correct: false, activeArm: null, phase: 'spurling_rotation', checks: {} };
  }

  // Use the same multi-method blended rotation calculation as the
  // neckRotationRangeOfMovement CV test (calculateNeckRotation from angleUtils).
  // Positive deg = left rotation, negative deg = right rotation.
  const rotationDeg = calculateNeckRotation(poseLandmarks);
  const rotationAngleDeg = Math.abs(rotationDeg);

  // 1. Rotation: minimum threshold to count as a deliberate rotation.
  const rotationOk = rotationAngleDeg >= SPURLINGS_ROTATION_THRESHOLD_DEG;
  const detectedSide = rotationDeg > 0 ? 'left' : (rotationDeg < 0 ? 'right' : null);

  // Determine side-bend opposite to the rotation side:
  //   rotationDeg > 0  → patient rotated to THEIR left, so side-bend right
  //                       near ear = right (8) should be lower than left (7)
  //   rotationDeg < 0  → patient rotated to THEIR right, so side-bend left
  //                       near ear = left (7) should be lower than right (8)
  const leftTiltDelta = (lEar.y || 0) - (rEar.y || 0);
  const rightTiltDelta = (rEar.y || 0) - (lEar.y || 0);

  if (!rotationOk || !detectedSide) {
    resetSpurlingsState();
    return {
      correct: false,
      activeArm: null,
      phase: 'spurling_rotation',
      checks: {
        rotation: rotationOk,
        rotationAngle: Math.round(rotationAngleDeg),
        tilt: false,
        noseMovedUp: false,
      },
    };
  }

  if (spurlingsState.activeSide && detectedSide !== spurlingsState.activeSide) {
    resetSpurlingsState();
  }
  spurlingsState.activeSide = detectedSide;

  const activeSide = spurlingsState.activeSide;
  const tiltDelta = activeSide === 'left' ? rightTiltDelta : leftTiltDelta;
  const tiltOk = tiltDelta > SPURLINGS_SIDEBEND_THRESHOLD;

  if (spurlingsState.phase === 'spurling_rotation') {
    spurlingsState.rotationFrames = Math.min(spurlingsState.rotationFrames + 1, 12);
    if (spurlingsState.rotationFrames >= 5) {
      spurlingsState.phase = 'spurling_sidebend';
      spurlingsState.tiltFrames = 0;
    }
    return {
      correct: false,
      activeArm: activeSide,
      phase: spurlingsState.phase,
      checks: {
        rotation: true,
        rotationAngle: Math.round(rotationAngleDeg),
        tilt: false,
        tiltDelta: Math.round(tiltDelta * 1000) / 1000,
        noseMovedUp: false,
      },
    };
  }

  if (!tiltOk) {
    spurlingsState.phase = 'spurling_sidebend';
    spurlingsState.tiltFrames = 0;
    spurlingsState.extensionFrames = 0;
    spurlingsState.baselineNoseY = null;
    return {
      correct: false,
      activeArm: activeSide,
      phase: 'spurling_sidebend',
      checks: {
        rotation: true,
        rotationAngle: Math.round(rotationAngleDeg),
        tilt: false,
        tiltDelta: Math.round(tiltDelta * 1000) / 1000,
        noseMovedUp: false,
      },
    };
  }

  if (spurlingsState.phase === 'spurling_sidebend') {
    spurlingsState.tiltFrames = Math.min(spurlingsState.tiltFrames + 1, 12);
    if (spurlingsState.tiltFrames >= 5) {
      spurlingsState.phase = 'spurling_extension';
      spurlingsState.baselineNoseY = nose.y || 0;
      spurlingsState.extensionFrames = 0;
    }
    return {
      correct: false,
      activeArm: activeSide,
      phase: spurlingsState.phase,
      checks: {
        rotation: true,
        rotationAngle: Math.round(rotationAngleDeg),
        tilt: true,
        tiltDelta: Math.round(tiltDelta * 1000) / 1000,
        noseMovedUp: false,
      },
    };
  }

  if (spurlingsState.baselineNoseY == null) {
    spurlingsState.baselineNoseY = nose.y || 0;
  }

  // 3. Upward nose movement: nose.y decreased from baseline (moved up on screen)
  const noseDelta = spurlingsState.baselineNoseY - (nose.y || 0);
  const noseMovedUp = noseDelta > SPURLINGS_EXTENSION_THRESHOLD;

  if (spurlingsState.phase === 'spurling_extension') {
    if (!noseMovedUp) {
      spurlingsState.extensionFrames = 0;
      return {
        correct: false,
        activeArm: activeSide,
        phase: 'spurling_extension',
        checks: {
          rotation: true,
          rotationAngle: Math.round(rotationAngleDeg),
          tilt: true,
          tiltDelta: Math.round(tiltDelta * 1000) / 1000,
          noseMovedUp: false,
          noseDelta: Math.round(noseDelta * 1000) / 1000,
        },
      };
    }

    spurlingsState.extensionFrames = Math.min(spurlingsState.extensionFrames + 1, 8);
    if (spurlingsState.extensionFrames >= 3) {
      spurlingsState.phase = 'spurling_complete';
    }
  }

  return {
    correct: spurlingsState.phase === 'spurling_complete',
    activeArm: activeSide,
    phase: spurlingsState.phase,
    checks: {
      rotation: true,
      rotationAngle: Math.round(rotationAngleDeg),
      tilt: true,
      tiltDelta: Math.round(tiltDelta * 1000) / 1000,
      noseMovedUp,
      noseDelta: Math.round(noseDelta * 1000) / 1000,
    },
  };
}

/**
 * Neck retraction repetition state.
 *
 * Tracks sideways orientation lock, baseline nose position, and
 * forward/retracted oscillation to count repetitions.
 */
const retractionState = {
  sidewaysFrames: 0,       // consecutive sideways-orientation frames
  sidewaysLocked: false,   // true once sustained sideways posture confirmed
  lockedSide: null,        // 'left' or 'right' — which side faces camera
  baselineNoseX: null,     // nose X when sideways first locked (resting position)
  phase: 'forward',        // 'forward' or 'retracted'
  repCount: 0,             // completed full retraction cycles
  requiredReps: 3,         // reps needed to pass
};

/**
 * Neck retraction repetition evaluation (two-phase detection).
 *
 * Phase 1 — Confirm sideways orientation:
 *   Patient sits sideways (same shoulder-width / z-depth logic as
 *   neckFlexionRangeOfMovement). Lock orientation after 8+ consecutive
 *   sideways frames.
 *
 * Phase 2 — Count retraction reps:
 *   In side view, measure nose X relative to a vertical shoulder line.
 *   Retraction = nose moves backward toward the shoulder (decreasing
 *   nose-to-shoulder horizontal gap).  A full rep = nose retracts past
 *   threshold, then returns forward past threshold.  After requiredReps
 *   completed → correct=true.
 *
 * Returns correct=true only when the patient has completed the required
 * number of retraction-return cycles in sideways posture.
 */
function evaluateNeckRetractionPose(poseLandmarks) {
  const nose      = poseLandmarks?.[0];
  const lShoulder = poseLandmarks?.[11];
  const rShoulder = poseLandmarks?.[12];

  if (!nose || !lShoulder || !rShoulder) {
    // Lost tracking — reset sideways lock but preserve rep count
    retractionState.sidewaysFrames = 0;
    retractionState.sidewaysLocked = false;
    retractionState.baselineNoseX = null;
    return {
      correct: false,
      activeArm: null,
      checks: { sideways: false, reps: retractionState.repCount, required: retractionState.requiredReps },
    };
  }

  // --- Sideways orientation detection (same logic as getPatientOrientation) ---
  const shoulderWidth = Math.abs((lShoulder.x || 0) - (rShoulder.x || 0));
  const zDiff = Math.abs((lShoulder.z || 0) - (rShoulder.z || 0));
  const isSideways = (shoulderWidth <= 0.13 && zDiff >= 0.04)
                  || (shoulderWidth <= 0.11)
                  || (zDiff >= 0.08)
                  || (zDiff >= 0.06 && shoulderWidth <= 0.13);

  if (!isSideways) {
    retractionState.sidewaysFrames = Math.max(0, retractionState.sidewaysFrames - 2);
    if (retractionState.sidewaysFrames <= 0) {
      retractionState.sidewaysLocked = false;
      retractionState.baselineNoseX = null;
    }
    return {
      correct: false,
      activeArm: retractionState.lockedSide,
      checks: { sideways: false, reps: retractionState.repCount, required: retractionState.requiredReps },
    };
  }

  retractionState.sidewaysFrames = Math.min(retractionState.sidewaysFrames + 1, 20);

  // Need 8+ consecutive sideways frames to lock orientation
  if (!retractionState.sidewaysLocked) {
    if (retractionState.sidewaysFrames >= 8) {
      retractionState.sidewaysLocked = true;
      // Determine which side faces camera: the shoulder with lower z is closer
      retractionState.lockedSide = (lShoulder.z || 0) <= (rShoulder.z || 0) ? 'left' : 'right';
      // Capture baseline: nose resting X position
      retractionState.baselineNoseX = nose.x || 0;
      retractionState.phase = 'forward';
      retractionState.repCount = 0;
    }
    return {
      correct: false,
      activeArm: retractionState.lockedSide,
      checks: { sideways: true, reps: 0, required: retractionState.requiredReps },
    };
  }

  // --- Phase 2: count retraction reps ---
  // Camera-facing shoulder is the reference vertical line.
  const cameraShoulder = retractionState.lockedSide === 'left'
    ? lShoulder : rShoulder;
  const shoulderX = cameraShoulder.x || 0;
  const noseX = nose.x || 0;

  // Horizontal distance from nose to camera-side shoulder (forward = larger gap)
  const noseToShoulder = Math.abs(noseX - shoulderX);
  const baselineGap = Math.abs((retractionState.baselineNoseX || noseX) - shoulderX);

  // Retraction threshold: nose moves ≥35% of baseline gap closer to shoulder
  // Return threshold: nose returns to within 15% of baseline gap
  const retractThreshold = baselineGap * 0.65; // gap shrinks to 65% of baseline
  const returnThreshold = baselineGap * 0.85;  // gap returns to 85% of baseline

  if (retractionState.phase === 'forward') {
    // Waiting for retraction: nose gap shrinks below threshold
    if (noseToShoulder <= retractThreshold && baselineGap > 0.01) {
      retractionState.phase = 'retracted';
    }
  } else if (retractionState.phase === 'retracted') {
    // Waiting for return: nose gap expands back above threshold
    if (noseToShoulder >= returnThreshold) {
      retractionState.repCount += 1;
      retractionState.phase = 'forward';
    }
  }

  const done = retractionState.repCount >= retractionState.requiredReps;

  return {
    correct: done,
    activeArm: retractionState.lockedSide,
    checks: {
      sideways: true,
      reps: retractionState.repCount,
      required: retractionState.requiredReps,
      phase: retractionState.phase,
    },
  };
}

// ---------------------------------------------------------------------------
// Neck retraction + extension repetition test  (repNeckRetractionExtentionEffect)
// ---------------------------------------------------------------------------
// Phase a) Patient sits sideways (same logic as neckFlexion/Extension)
// Phase b) Chin tucks toward shoulder (retraction), then neck extends backward,
//          then returns to neutral. Count 3 full retract→extend→return cycles.
// ---------------------------------------------------------------------------
let retractionExtensionState = {
  sidewaysFrames: 0,
  sidewaysLocked: false,
  lockedSide: null,
  baselineNoseX: null,
  phase: 'neutral',        // 'neutral' → 'retracted' → 'extended' → 'neutral'
  repCount: 0,
  requiredReps: 3,
  baselineExtension: null,  // baseline extension angle from neutral
};

function evaluateNeckRetractionExtensionPose(poseLandmarks) {
  const nose      = poseLandmarks?.[0];
  const lShoulder = poseLandmarks?.[11];
  const rShoulder = poseLandmarks?.[12];

  if (!nose || !lShoulder || !rShoulder) {
    retractionExtensionState.sidewaysFrames = 0;
    retractionExtensionState.sidewaysLocked = false;
    retractionExtensionState.baselineNoseX = null;
    retractionExtensionState.baselineExtension = null;
    return {
      correct: false, activeArm: null,
      checks: { sideways: false, reps: retractionExtensionState.repCount, required: retractionExtensionState.requiredReps },
    };
  }

  // --- Sideways orientation detection ---
  const shoulderWidth = Math.abs((lShoulder.x || 0) - (rShoulder.x || 0));
  const zDiff = Math.abs((lShoulder.z || 0) - (rShoulder.z || 0));
  const isSideways = (shoulderWidth <= 0.13 && zDiff >= 0.04)
                  || (shoulderWidth <= 0.11)
                  || (zDiff >= 0.08)
                  || (zDiff >= 0.06 && shoulderWidth <= 0.13);

  if (!isSideways) {
    retractionExtensionState.sidewaysFrames = Math.max(0, retractionExtensionState.sidewaysFrames - 2);
    if (retractionExtensionState.sidewaysFrames <= 0) {
      retractionExtensionState.sidewaysLocked = false;
      retractionExtensionState.baselineNoseX = null;
      retractionExtensionState.baselineExtension = null;
    }
    return {
      correct: false, activeArm: retractionExtensionState.lockedSide,
      checks: { sideways: false, reps: retractionExtensionState.repCount, required: retractionExtensionState.requiredReps },
    };
  }

  retractionExtensionState.sidewaysFrames = Math.min(retractionExtensionState.sidewaysFrames + 1, 20);

  if (!retractionExtensionState.sidewaysLocked) {
    if (retractionExtensionState.sidewaysFrames >= 8) {
      retractionExtensionState.sidewaysLocked = true;
      retractionExtensionState.lockedSide = (lShoulder.z || 0) <= (rShoulder.z || 0) ? 'left' : 'right';
      retractionExtensionState.baselineNoseX = nose.x || 0;
      retractionExtensionState.baselineExtension = calculateNeckExtension(poseLandmarks);
      retractionExtensionState.phase = 'neutral';
      retractionExtensionState.repCount = 0;
    }
    return {
      correct: false, activeArm: retractionExtensionState.lockedSide,
      checks: { sideways: true, reps: 0, required: retractionExtensionState.requiredReps },
    };
  }

  // --- Phase 2: count retraction→extension→return cycles ---
  const cameraShoulder = retractionExtensionState.lockedSide === 'left' ? lShoulder : rShoulder;
  const shoulderX = cameraShoulder.x || 0;
  const noseX = nose.x || 0;
  const noseToShoulder = Math.abs(noseX - shoulderX);
  const baselineGap = Math.abs((retractionExtensionState.baselineNoseX || noseX) - shoulderX);

  const retractThreshold = baselineGap * 0.80; // gap shrinks to 80% of baseline (eased)
  const extensionAngle = calculateNeckExtension(poseLandmarks);
  const baseExt = retractionExtensionState.baselineExtension || 0;
  const extensionGain = extensionAngle - baseExt;  // degrees gained from neutral

  if (retractionExtensionState.phase === 'neutral') {
    // Waiting for retraction: nose gap shrinks below threshold
    if (noseToShoulder <= retractThreshold && baselineGap > 0.01) {
      retractionExtensionState.phase = 'retracted';
    }
  } else if (retractionExtensionState.phase === 'retracted') {
    // Waiting for extension: head tilts back ≥15° beyond baseline
    if (extensionGain >= 15) {
      retractionExtensionState.phase = 'extended';
    }
  } else if (retractionExtensionState.phase === 'extended') {
    // Waiting for return to neutral: extension drops back below 8° gain AND nose gap returns
    if (extensionGain < 8 && noseToShoulder >= baselineGap * 0.80) {
      retractionExtensionState.repCount += 1;
      retractionExtensionState.phase = 'neutral';
    }
  }

  const done = retractionExtensionState.repCount >= retractionExtensionState.requiredReps;

  return {
    correct: done,
    activeArm: retractionExtensionState.lockedSide,
    checks: {
      sideways: true,
      reps: retractionExtensionState.repCount,
      required: retractionExtensionState.requiredReps,
      phase: retractionExtensionState.phase,
    },
  };
}

// ---------------------------------------------------------------------------
// Neck repeated extension test  (neck_repExtentionTest)
// ---------------------------------------------------------------------------
// Sideways orientation, then count 3 reps of neck extension (head tilts back
// past threshold, then returns to near-neutral).
// Uses calculateNeckExtension from angleUtils.js.
// ---------------------------------------------------------------------------
let repExtensionState = {
  sidewaysFrames: 0,
  sidewaysLocked: false,
  lockedSide: null,
  baselineExtension: null,
  phase: 'neutral',         // 'neutral' → 'extended' → 'neutral'
  repCount: 0,
  requiredReps: 3,
};

function evaluateRepExtensionPose(poseLandmarks) {
  const nose      = poseLandmarks?.[0];
  const lShoulder = poseLandmarks?.[11];
  const rShoulder = poseLandmarks?.[12];

  if (!nose || !lShoulder || !rShoulder) {
    repExtensionState.sidewaysFrames = 0;
    repExtensionState.sidewaysLocked = false;
    repExtensionState.baselineExtension = null;
    return {
      correct: false, activeArm: null,
      checks: { sideways: false, reps: repExtensionState.repCount, required: repExtensionState.requiredReps },
    };
  }

  const shoulderWidth = Math.abs((lShoulder.x || 0) - (rShoulder.x || 0));
  const zDiff = Math.abs((lShoulder.z || 0) - (rShoulder.z || 0));
  const isSideways = (shoulderWidth <= 0.13 && zDiff >= 0.04)
                  || (shoulderWidth <= 0.11)
                  || (zDiff >= 0.08)
                  || (zDiff >= 0.06 && shoulderWidth <= 0.13);

  if (!isSideways) {
    repExtensionState.sidewaysFrames = Math.max(0, repExtensionState.sidewaysFrames - 2);
    if (repExtensionState.sidewaysFrames <= 0) {
      repExtensionState.sidewaysLocked = false;
      repExtensionState.baselineExtension = null;
    }
    return {
      correct: false, activeArm: repExtensionState.lockedSide,
      checks: { sideways: false, reps: repExtensionState.repCount, required: repExtensionState.requiredReps },
    };
  }

  repExtensionState.sidewaysFrames = Math.min(repExtensionState.sidewaysFrames + 1, 20);

  if (!repExtensionState.sidewaysLocked) {
    if (repExtensionState.sidewaysFrames >= 8) {
      repExtensionState.sidewaysLocked = true;
      repExtensionState.lockedSide = (lShoulder.z || 0) <= (rShoulder.z || 0) ? 'left' : 'right';
      repExtensionState.baselineExtension = calculateNeckExtension(poseLandmarks);
      repExtensionState.phase = 'neutral';
      repExtensionState.repCount = 0;
    }
    return {
      correct: false, activeArm: repExtensionState.lockedSide,
      checks: { sideways: true, reps: 0, required: repExtensionState.requiredReps },
    };
  }

  // --- Count extension reps ---
  const extensionAngle = calculateNeckExtension(poseLandmarks);
  const baseExt = repExtensionState.baselineExtension || 0;
  const extensionGain = extensionAngle - baseExt;

  if (repExtensionState.phase === 'neutral') {
    // Waiting for extension: head tilts back ≥15° beyond baseline
    if (extensionGain >= 15) {
      repExtensionState.phase = 'extended';
    }
  } else if (repExtensionState.phase === 'extended') {
    // Waiting for return: extension drops back below 8° gain
    if (extensionGain < 8) {
      repExtensionState.repCount += 1;
      repExtensionState.phase = 'neutral';
    }
  }

  const done = repExtensionState.repCount >= repExtensionState.requiredReps;

  return {
    correct: done,
    activeArm: repExtensionState.lockedSide,
    checks: {
      sideways: true,
      reps: repExtensionState.repCount,
      required: repExtensionState.requiredReps,
      phase: repExtensionState.phase,
    },
  };
}

// ---------------------------------------------------------------------------
// Neck repeated flexion test  (repNeckFlexionEffect)
// ---------------------------------------------------------------------------
// Sideways orientation, then count 3 reps of neck flexion (chin toward chest
// past threshold, then returns to near-neutral).
// Uses calculateNeckFlexionSide from angleUtils.js.
// ---------------------------------------------------------------------------
let repFlexionState = {
  sidewaysFrames: 0,
  sidewaysLocked: false,
  lockedSide: null,
  baselineFlexion: null,
  phase: 'neutral',          // 'neutral' → 'flexed' → 'neutral'
  repCount: 0,
  requiredReps: 3,
};

function evaluateRepFlexionPose(poseLandmarks) {
  const nose      = poseLandmarks?.[0];
  const lShoulder = poseLandmarks?.[11];
  const rShoulder = poseLandmarks?.[12];

  if (!nose || !lShoulder || !rShoulder) {
    repFlexionState.sidewaysFrames = 0;
    repFlexionState.sidewaysLocked = false;
    repFlexionState.baselineFlexion = null;
    return {
      correct: false, activeArm: null,
      checks: { sideways: false, reps: repFlexionState.repCount, required: repFlexionState.requiredReps },
    };
  }

  const shoulderWidth = Math.abs((lShoulder.x || 0) - (rShoulder.x || 0));
  const zDiff = Math.abs((lShoulder.z || 0) - (rShoulder.z || 0));
  const isSideways = (shoulderWidth <= 0.13 && zDiff >= 0.04)
                  || (shoulderWidth <= 0.11)
                  || (zDiff >= 0.08)
                  || (zDiff >= 0.06 && shoulderWidth <= 0.13);

  if (!isSideways) {
    repFlexionState.sidewaysFrames = Math.max(0, repFlexionState.sidewaysFrames - 2);
    if (repFlexionState.sidewaysFrames <= 0) {
      repFlexionState.sidewaysLocked = false;
      repFlexionState.baselineFlexion = null;
    }
    return {
      correct: false, activeArm: repFlexionState.lockedSide,
      checks: { sideways: false, reps: repFlexionState.repCount, required: repFlexionState.requiredReps },
    };
  }

  repFlexionState.sidewaysFrames = Math.min(repFlexionState.sidewaysFrames + 1, 20);

  if (!repFlexionState.sidewaysLocked) {
    if (repFlexionState.sidewaysFrames >= 8) {
      repFlexionState.sidewaysLocked = true;
      repFlexionState.lockedSide = (lShoulder.z || 0) <= (rShoulder.z || 0) ? 'left' : 'right';
      repFlexionState.baselineFlexion = calculateNeckFlexionSide(poseLandmarks);
      repFlexionState.phase = 'neutral';
      repFlexionState.repCount = 0;
    }
    return {
      correct: false, activeArm: repFlexionState.lockedSide,
      checks: { sideways: true, reps: 0, required: repFlexionState.requiredReps },
    };
  }

  // --- Count flexion reps ---
  const flexionAngle = calculateNeckFlexionSide(poseLandmarks);
  const baseFlex = repFlexionState.baselineFlexion || 0;
  const flexionGain = flexionAngle - baseFlex;

  if (repFlexionState.phase === 'neutral') {
    // Waiting for flexion: chin tilts forward ≥15° beyond baseline
    if (flexionGain >= 15) {
      repFlexionState.phase = 'flexed';
    }
  } else if (repFlexionState.phase === 'flexed') {
    // Waiting for return: flexion drops back below 8° gain
    if (flexionGain < 8) {
      repFlexionState.repCount += 1;
      repFlexionState.phase = 'neutral';
    }
  }

  const done = repFlexionState.repCount >= repFlexionState.requiredReps;

  return {
    correct: done,
    activeArm: repFlexionState.lockedSide,
    checks: {
      sideways: true,
      reps: repFlexionState.repCount,
      required: repFlexionState.requiredReps,
      phase: repFlexionState.phase,
    },
  };
}

// ── Shoulder-shrug + neck rotation pose-check ──────────────────────────
// Patient faces the camera, shrugs shoulders upward, then rotates neck.
// Correct = rotation ≥ 15° AND shoulders elevated ≥ 0.015 normalised-Y
// compared to the baseline captured in the first stable front-facing frames.
const shoulderShrugRotationState = {
  baselineLShoulderY: null,
  baselineRShoulderY: null,
  stableFrames: 0,        // front-facing frames used to lock baseline
  baselineLocked: false,
};

function evaluateShoulderShrugRotationPose(poseLandmarks) {
  const nose      = poseLandmarks?.[0];
  const lShoulder = poseLandmarks?.[11];
  const rShoulder = poseLandmarks?.[12];

  if (!nose || !lShoulder || !rShoulder) {
    return { correct: false, activeArm: null, checks: { front: false, rotation: false, shrug: false } };
  }

  // Must be front-facing (use getPatientOrientation)
  const orientation = getPatientOrientation(poseLandmarks);
  if (orientation !== 'front') {
    return { correct: false, activeArm: null, checks: { front: false, rotation: false, shrug: false } };
  }

  // Lock baseline shoulder Y after 6 stable front-facing frames
  if (!shoulderShrugRotationState.baselineLocked) {
    shoulderShrugRotationState.stableFrames += 1;
    if (shoulderShrugRotationState.stableFrames >= 6) {
      shoulderShrugRotationState.baselineLShoulderY = lShoulder.y || 0;
      shoulderShrugRotationState.baselineRShoulderY = rShoulder.y || 0;
      shoulderShrugRotationState.baselineLocked = true;
    }
    return { correct: false, activeArm: null, checks: { front: true, rotation: false, shrug: false, calibrating: true } };
  }

  // Check rotation (reuse existing calculateNeckRotation)
  const rotationDeg = calculateNeckRotation(poseLandmarks);
  const rotationAngle = Math.abs(rotationDeg);
  const rotationOk = rotationAngle >= 15;

  // Check shoulder shrug: both shoulders should be higher (lower Y value in
  // normalised coords) than baseline by at least 0.015 units.
  const lShoulderRise = (shoulderShrugRotationState.baselineLShoulderY) - (lShoulder.y || 0);
  const rShoulderRise = (shoulderShrugRotationState.baselineRShoulderY) - (rShoulder.y || 0);
  const shrugThreshold = 0.015;
  const shrugOk = lShoulderRise >= shrugThreshold && rShoulderRise >= shrugThreshold;

  const checks = {
    front: true,
    rotation: rotationOk,
    rotationAngle: Math.round(rotationAngle),
    shrug: shrugOk,
    lShoulderRise: Math.round(lShoulderRise * 1000) / 1000,
    rShoulderRise: Math.round(rShoulderRise * 1000) / 1000,
  };

  return {
    correct: rotationOk && shrugOk,
    activeArm: rotationDeg > 0 ? 'left' : (rotationDeg < 0 ? 'right' : null),
    checks,
  };
}

// ── MWM (Mobilization With Movement) neck rotation pose-check ──────────
// Patient faces the camera, places one hand on the back of the neck,
// then rotates the neck. Correct = rotation ≥ 15° AND hand near neck detected.
function evaluateMWMNeckRotationPose(poseLandmarks, handLandmarksArray) {
  const nose      = poseLandmarks?.[0];
  const lShoulder = poseLandmarks?.[11];
  const rShoulder = poseLandmarks?.[12];
  const lEar      = poseLandmarks?.[7];
  const rEar      = poseLandmarks?.[8];

  if (!nose || !lShoulder || !rShoulder) {
    return { correct: false, activeArm: null, checks: { front: false, rotation: false, handOnNeck: false } };
  }

  // Must be front-facing
  const orientation = getPatientOrientation(poseLandmarks);
  if (orientation !== 'front') {
    return { correct: false, activeArm: null, checks: { front: false, rotation: false, handOnNeck: false } };
  }

  // Check rotation
  const rotationDeg = calculateNeckRotation(poseLandmarks);
  const rotationAngle = Math.abs(rotationDeg);
  const rotationOk = rotationAngle >= 15;

  // Check hand behind/on neck:
  // A wrist (pose landmarks 15 or 16) should be near the neck area —
  // vertically between ears and shoulders, horizontally near the midline
  // of the shoulders.
  const shoulderMidX = ((lShoulder.x || 0) + (rShoulder.x || 0)) / 2;
  const shoulderMidY = ((lShoulder.y || 0) + (rShoulder.y || 0)) / 2;
  const earMidY = (lEar && rEar)
    ? ((lEar.y || 0) + (rEar.y || 0)) / 2
    : (nose.y || 0);

  // Neck region: X within 0.15 of shoulder midline, Y between ear level and shoulder level
  const neckTop = earMidY;
  const neckBottom = shoulderMidY + 0.03; // slight margin below shoulders

  let handOnNeck = false;
  let handArm = null;

  // Check pose wrist landmarks (15 = left wrist, 16 = right wrist)
  const wristDefs = [
    { idx: 15, name: 'left' },
    { idx: 16, name: 'right' },
  ];
  for (const wd of wristDefs) {
    const wrist = poseLandmarks?.[wd.idx];
    if (!wrist) continue;
    const wx = wrist.x || 0;
    const wy = wrist.y || 0;
    const nearMidline = Math.abs(wx - shoulderMidX) < 0.18;
    const inNeckZone = wy >= (neckTop - 0.03) && wy <= neckBottom;
    // Also check z: if wrist z is significantly behind the shoulder plane,
    // it is more likely behind the neck. Accept even if z is similar (hand on side of neck).
    if (nearMidline && inNeckZone) {
      handOnNeck = true;
      handArm = wd.name;
      break;
    }
  }

  // Fallback: if hand landmarks are available, check if any fingertip is in the neck zone
  if (!handOnNeck && handLandmarksArray && handLandmarksArray.length > 0) {
    for (const hl of handLandmarksArray) {
      if (!hl || !hl[0]) continue;
      // Use wrist (0) and middle finger tip (12)
      for (const tipIdx of [0, 12]) {
        const tip = hl[tipIdx];
        if (!tip) continue;
        const tx = tip.x || 0;
        const ty = tip.y || 0;
        if (Math.abs(tx - shoulderMidX) < 0.18 && ty >= (neckTop - 0.03) && ty <= neckBottom) {
          handOnNeck = true;
          break;
        }
      }
      if (handOnNeck) break;
    }
  }

  const checks = {
    front: true,
    rotation: rotationOk,
    rotationAngle: Math.round(rotationAngle),
    handOnNeck,
  };

  return {
    correct: rotationOk && handOnNeck,
    activeArm: rotationDeg > 0 ? 'left' : (rotationDeg < 0 ? 'right' : null),
    checks,
  };
}

function overlayInstruction(identifier, testName, phaseName, orientation, options = {}) {
  if (options.isMovementComplete) return options.completedText || '✅ Movement completed';

  const id = (identifier || '').toLowerCase();
  const tn = (testName || '').toLowerCase();

  // Bakody pose-check: static instruction
  if (id === 'neck_bakody' || tn === 'bakody_pose_check') {
    return 'Place your affected arm on top of your head as shown in the picture.';
  }

  // Vasalva pose-check: static instruction
  if (id === 'neck_vasalva' || tn === 'vasalva_pose_check') {
    return 'Hold your hand in front of your mouth and blow against it as shown in the picture.';
  }

  // ULNTT pose-check: per-test instructions
  if (id === 'ulntt_neck_medianus' || tn === 'ulntt_medianus_pose_check') {
    return 'Raise your painful arm out to the side at shoulder height, extend your wrist back, and tilt your head away from that arm.';
  }
  if (id === 'ulntt_neck_radialis' || tn === 'ulntt_radialis_pose_check') {
    return 'Keep your head straight. Let your arm hang with fingers pointing down towards your hip, wrist bent back, pinky side closest to your leg.';
  }
  if (id === 'ulntt_neck_ulnaris' || tn === 'ulntt_ulnaris_pose_check') {
    return 'Keep your head straight. Raise your arm to the side, bend your elbow so fingers point towards your ear, and extend your wrist back.';
  }

  // Spurling's test: rotation + tilt + extension
  if (id === 'neck_spurlingstest_2' || tn === 'spurlings_pose_check') {
    if (phaseName === 'spurling_sidebend') {
      return '2/3 Good. Keep rotated and side-bend your head away from the painful side.';
    }
    if (phaseName === 'spurling_extension') {
      return '3/3 Good. Keep rotated and side-bent, then tilt your head backwards.';
    }
    if (phaseName === 'spurling_complete') {
      return '✅ Spurling posture recognized. Hold this position.';
    }
    return '1/3 Rotate your head towards the painful side.';
  }

  // Neck retraction repetition test
  if (id === 'repneckretractioneffect' || tn === 'neck_retraction_pose_check') {
    return 'Sit sideways to the camera. Slowly tuck your chin back and return. Repeat a few times.';
  }

  // Neck retraction + extension repetition test
  if (id === 'repneckretractionextentioneffect' || tn === 'neck_retraction_extension_pose_check') {
    return 'Sit sideways to the camera. Tuck your chin back, then extend your neck backward, and return. Repeat a few times.';
  }

  // Neck repeated extension test
  if (id === 'neck_repextentiontest' || tn === 'neck_rep_extension_pose_check') {
    return 'Sit sideways to the camera. Slowly lean your head backward looking up, then return. Repeat a few times.';
  }

  // Neck repeated flexion test
  if (id === 'repneckflexioneffect' || tn === 'neck_rep_flexion_pose_check') {
    return 'Sit sideways to the camera. Slowly bend your neck forward toward your chest, then return. Repeat a few times.';
  }

  // Shoulder shrug + neck rotation pose-check
  if (id === 'shouldershrugtestimprovepainfreerotation' || tn === 'shoulder_shrug_rotation_pose_check') {
    return 'Face the camera. Shrug both shoulders up toward your ears, then rotate your head to the painful side.';
  }

  // MWM neck rotation pose-check
  if (id === 'nonspecificmwmneckrotation' || tn === 'mwm_neck_rotation_pose_check') {
    return 'Face the camera. Place one hand on the back of your neck, then rotate your head to the painful side.';
  }

  const isFlexion = id.includes('neckflexionrangeofmovement') || tn === 'neck_flexion_side';
  const isExtension = id.includes('neckextentionrangeofmovement') || tn === 'neck_extension';

  // For sagittal flexion/extension, keep guidance text sourced only from
  // the dedicated movement/orientation branches below.
  if (options.startPoseText && !options.startPoseLocked && !isFlexion && !isExtension) {
    return options.startPoseText;
  }

  if (isFlexion) {
    if (orientation !== 'sideways') return 'Please sit sideways to the camera to measure.';
    return 'Bend your neck slowly towards your chest-bone and return.';
  }

  if (isExtension) {
    if (orientation !== 'sideways') return 'Please sit sideways to the camera to measure.';
    return 'Move your neck slowly backward and look up to the ceiling.';
  }

  if (id.includes('neckrotationrangeofmovement') || tn === 'neck_rotation') {
    if (phaseName === 'rotation_left') return 'Face the camera. Turn your head to the left as far as comfortable.';
    if (phaseName === 'rotation_right') return 'Face the camera. Turn your head to the right as far as comfortable.';
    return 'Face the camera. Turn your head left and right as far as comfortable.';
  }

  return 'Move slowly and keep your upper body in frame.';
}

function evaluateStartPose({ identifier, testName, calcType, landmarks, worldLandmarks }) {
  const id = (identifier || '').toLowerCase();
  const tn = (testName || '').toLowerCase();
  const calc = (calcType || '').toLowerCase();

  const isRotation = id.includes('neckrotationrangeofmovement') || tn === 'neck_rotation' || calc === 'neck_rotation';
  const isFlexion = id.includes('neckflexionrangeofmovement') || tn === 'neck_flexion_side' || calc === 'neck_flexion_side' || calc === 'neck_sagittal_side';
  const isExtension = id.includes('neckextentionrangeofmovement') || tn === 'neck_extension' || calc === 'neck_extension';
  const requiresStartPose = isRotation || isFlexion || isExtension;
  if (!requiresStartPose) return { required: false, correct: true, text: '' };

  const nose = (worldLandmarks && worldLandmarks[0]) || landmarks?.[0];
  const lShoulder = (worldLandmarks && worldLandmarks[11]) || landmarks?.[11];
  const rShoulder = (worldLandmarks && worldLandmarks[12]) || landmarks?.[12];
  if (!nose || !lShoulder || !rShoulder) {
    return { required: true, correct: false, text: 'Position your upper body fully in frame.' };
  }

  const usesWorld = Boolean(worldLandmarks && worldLandmarks[0] && worldLandmarks[11] && worldLandmarks[12]);
  const orientation2d = getPatientOrientation(landmarks);
  const shoulderWidth = Math.abs((lShoulder.x || 0) - (rShoulder.x || 0));
  const shoulderZDiff = Math.abs((lShoulder.z || 0) - (rShoulder.z || 0));
  const shoulderMidX = ((lShoulder.x || 0) + (rShoulder.x || 0)) / 2;
  const noseOffsetNorm = Math.abs((nose.x || 0) - shoulderMidX) / Math.max(shoulderWidth, 1e-6);

  if (isRotation) {
    // Front-facing + head neutral before measuring rotation.
    const frontOk = usesWorld
      ? (shoulderWidth >= 0.18 && shoulderZDiff <= 0.08)
      : false;
    const orientationFrontOk = orientation2d === 'front';
    const headNeutralOk = noseOffsetNorm <= 0.28;
    if (!(frontOk || orientationFrontOk)) {
      return { required: true, correct: false, text: 'Starting position: face the camera directly.' };
    }
    if (!headNeutralOk) {
      return { required: true, correct: false, text: 'Starting position: keep your head centered and neutral.' };
    }
    return { required: true, correct: true, text: 'Sit tall and upright. Hold still for 2 seconds to capture baseline.' };
  }

  // Sagittal: side profile to camera and centered before baseline lock.
  const sideOk = usesWorld
    ? (shoulderZDiff >= 0.05 || shoulderWidth <= 0.24)
    : false;
  const orientationSideOk = orientation2d === 'sideways';
  if (!(sideOk || orientationSideOk)) {
    return { required: true, correct: false, text: 'Starting position: sit sideways to the camera.' };
  }
  return { required: true, correct: true, text: 'Sit tall and upright. Hold still for 2 seconds to capture baseline.' };
}

function getPatientOrientation(landmarks) {
  const nose = landmarks?.[0];
  const lShoulder = landmarks?.[11];
  const rShoulder = landmarks?.[12];
  if (!nose || !lShoulder || !rShoulder) return 'unknown';
  const lHip = landmarks?.[23];
  const rHip = landmarks?.[24];

  // Combine depth asymmetry and apparent width. Single metrics can be noisy.
  const zDiff = Math.abs((lShoulder.z || 0) - (rShoulder.z || 0));
  const shoulderWidth = Math.abs((lShoulder.x || 0) - (rShoulder.x || 0));
  const hipWidth = (lHip && rHip) ? Math.abs((lHip.x || 0) - (rHip.x || 0)) : null;
  const shoulderCenterX = ((lShoulder.x || 0) + (rShoulder.x || 0)) / 2;
  const noseOffset = Math.abs((nose.x || 0) - shoulderCenterX);

  // Hard gates first: these prevent false sideways when user is actually front-facing.
  if (shoulderWidth >= 0.16) return 'front';
  if (shoulderWidth <= 0.11 && zDiff >= 0.04) return 'sideways';

  // Strong front cues
  if (shoulderWidth >= 0.18 && noseOffset <= 0.10 && zDiff <= 0.06) return 'front';
  if (shoulderWidth >= 0.20 && zDiff <= 0.04) return 'front';
  if (hipWidth != null && hipWidth >= 0.16 && zDiff <= 0.04) return 'front';
  if (zDiff <= 0.02) return 'front';

  // Strong side cues
  if (noseOffset >= 0.14 && shoulderWidth <= 0.13 && zDiff >= 0.04) return 'sideways';
  if (zDiff >= 0.08) return 'sideways';
  if (zDiff >= 0.06 && shoulderWidth <= 0.13) return 'sideways';
  if (hipWidth != null && shoulderWidth <= 0.13 && hipWidth <= 0.12) return 'sideways';

  return 'front';
}

function detectSagittalSideFromShoulders(landmarks, worldLandmarks) {
  const lShoulder = (worldLandmarks && worldLandmarks[11]) || landmarks?.[11];
  const rShoulder = (worldLandmarks && worldLandmarks[12]) || landmarks?.[12];
  if (!lShoulder || !rShoulder) return null;
  return (lShoulder.z || 0) <= (rShoulder.z || 0) ? 'left' : 'right';
}

function computeSagittalBaselineMetrics(landmarks, worldLandmarks, lockedSide = null) {
  const side = lockedSide || detectSagittalSideFromShoulders(landmarks, worldLandmarks);
  if (!side) return null;

  const useLeft = side === 'left';
  const ear = useLeft ? landmarks?.[7] : landmarks?.[8];
  const shoulder = useLeft ? landmarks?.[11] : landmarks?.[12];
  const hip = useLeft ? landmarks?.[23] : landmarks?.[24];
  const nose = landmarks?.[0];
  if (!ear || !shoulder || !hip || !nose) return null;

  // Closer to 0 means closer to the requested baseline posture:
  // ear-nose closer to horizontal, torso closer to vertical.
  const earNoseHorizontalDeviation = Math.abs((nose.y || 0) - (ear.y || 0));
  const torsoVerticalDeviation = Math.abs((hip.x || 0) - (shoulder.x || 0));

  if (!Number.isFinite(earNoseHorizontalDeviation) || !Number.isFinite(torsoVerticalDeviation)) {
    return null;
  }

  return {
    side,
    earNoseHorizontalDeviation,
    torsoVerticalDeviation,
  };
}

function computeNoseEarSimilarity(landmarks) {
  const nose = landmarks?.[0];
  const lEar = landmarks?.[7];
  const rEar = landmarks?.[8];
  if (!nose || !lEar || !rEar) return null;

  const dist2d = (a, b) => {
    const dx = (a.x || 0) - (b.x || 0);
    const dy = (a.y || 0) - (b.y || 0);
    return Math.hypot(dx, dy);
  };

  const left = dist2d(nose, lEar);
  const right = dist2d(nose, rEar);
  const mx = Math.max(left, right);
  if (mx <= 1e-6) return null;
  return Math.min(left, right) / mx; // 1.0 = perfectly symmetric
}

function computeRotationBaselineMetrics(landmarks) {
  const nose = landmarks?.[0];
  const lEar = landmarks?.[7];
  const rEar = landmarks?.[8];
  const lShoulder = landmarks?.[11];
  const rShoulder = landmarks?.[12];
  if (!nose || !lEar || !rEar || !lShoulder || !rShoulder) return null;

  const earMidX = ((lEar.x || 0) + (rEar.x || 0)) / 2;
  const earMidY = ((lEar.y || 0) + (rEar.y || 0)) / 2;
  const shoulderMidX = ((lShoulder.x || 0) + (rShoulder.x || 0)) / 2;
  const shoulderMidY = ((lShoulder.y || 0) + (rShoulder.y || 0)) / 2;
  const headMidX = ((nose.x || 0) + earMidX) / 2;
  const headMidY = ((nose.y || 0) + earMidY) / 2;

  // Baseline quality cues for neutral upright + centered head:
  // 1) head midline centered over shoulder midpoint vertical
  // 2) nose-earMid line close to horizontal
  // 3) head held tall (greater shoulder-to-head vertical separation)
  const centerDeviation = Math.abs(headMidX - shoulderMidX);
  const headLineHorizontalDeviation = Math.abs((nose.y || 0) - earMidY);
  const headHeight = shoulderMidY - headMidY;

  if (!Number.isFinite(centerDeviation)
    || !Number.isFinite(headLineHorizontalDeviation)
    || !Number.isFinite(headHeight)) {
    return null;
  }

  return {
    centerDeviation,
    headLineHorizontalDeviation,
    headHeight,
  };
}

export default function ComputerVisionTest({ data, selectedChoiceCallback, embedded, onCvComplete, onAngleUpdate, autoStartSignal, freeMeasure = false, manualSubmitSignal = 0, embeddedOverlayContent = null, onStatusChange }) {
  const classes = useStyles();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const landmarkerRef = useRef(null);
  const faceLandmarkerRef = useRef(null);
  const handLandmarkerRef = useRef(null);
  const trackerRef = useRef(null);
  const rafIdRef = useRef(null);
  const streamRef = useRef(null);
  const handleContinueRef = useRef(null);
  const onAngleUpdateRef = useRef(onAngleUpdate);
  const lastManualSubmitSignalRef = useRef(manualSubmitSignal);
  const pendingManualSubmitRef = useRef(false);
  // Baseline for the video overlay display: first detected angle is zeroed so
  // the overlay shows movement from the patient's starting position (not the
  // absolute nose-shoulder angle which starts at ~15° for sagittal tests).
  const overlayBaselineRef = useRef(null);
  const startPoseHoldStartRef = useRef(null);
  const startPoseLockedRef = useRef(false);
  const startPoseLockedAtRef = useRef(null);
  const sagittalSideLockedRef = useRef(null);
  const sagittalSideCandidateRef = useRef(null);
  const sagittalSideCandidateSinceRef = useRef(null);
  const movementCompleteRef = useRef(false);
  const lastSpokenTextRef = useRef('');
  const promptLastTextRef = useRef('');
  const promptLastSetAtRef = useRef(0);

  const [status, setStatus] = useState('loading'); // loading | ready | running | submitting | done | error
  const [currentPhase, setCurrentPhase] = useState('');
  const [violations, setViolations] = useState([]);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [orientationForSagittal, setOrientationForSagittal] = useState('front');
  const [startPoseText, setStartPoseText] = useState('');
  const [startPoseLocked, setStartPoseLocked] = useState(false);
  const [startPoseCorrect, setStartPoseCorrect] = useState(false);
  const [movementComplete, setMovementComplete] = useState(false);
  const sidewaysScoreRef = useRef(0);

  const cvTestConfig = data.cvTestConfig;
  const identifier = data.identifier || '';
  const side = data.cvSide || data.side || 'left';
  const instructions = data.cvInstructions || data.instructions || '';
  const answerMapping = data.cvAnswerMapping || null;
  const mappingMode = data.cvAnswerMappingMode || 'cv_guided';
  const isGuided = mappingMode === 'cv_guided';
  const [guidanceText, setGuidanceText] = useState('');
  const startPoseRequired = useMemo(() => {
    const id = (identifier || '').toLowerCase();
    const tn = (cvTestConfig?.testName || '').toLowerCase();
    const calc = (cvTestConfig?.landmarkConfig?.calculation_type || '').toLowerCase();
    return id.includes('neckrotationrangeofmovement')
      || id.includes('neckflexionrangeofmovement')
      || id.includes('neckextentionrangeofmovement')
      || tn === 'neck_rotation'
      || tn === 'neck_flexion_side'
      || tn === 'neck_extension'
      || calc === 'neck_rotation'
      || calc === 'neck_flexion_side'
      || calc === 'neck_extension'
      || calc === 'neck_sagittal_side';
  }, [identifier, cvTestConfig]);

  const overlayText = overlayInstruction(identifier, cvTestConfig?.testName, currentPhase, orientationForSagittal, {
    startPoseText,
    startPoseLocked,
    isMovementComplete: movementComplete,
    completedText: '✅ Good job. Try a few times, then return facing the camera.',
  });

  const speakText = useCallback((text) => {
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
    const normalized = text.replace('✅', '').replace('⚠', 'Warning:').trim();
    if (!normalized || normalized === lastSpokenTextRef.current) return;
    lastSpokenTextRef.current = normalized;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(normalized);
      utter.rate = 0.95;
      utter.pitch = 1.0;
      window.speechSynthesis.speak(utter);
    } catch (_) {}
  }, []);

  const setStableStartPoseText = useCallback((nextText, options = {}) => {
    if (!nextText) return;
    const force = Boolean(options.force);
    const minHoldMs = options.minHoldMs ?? 900;
    const now = Date.now();
    const prevText = promptLastTextRef.current;
    const sinceLast = now - promptLastSetAtRef.current;

    if (!force && prevText && nextText !== prevText && sinceLast < minHoldMs) {
      return;
    }
    if (nextText === prevText) return;

    promptLastTextRef.current = nextText;
    promptLastSetAtRef.current = now;
    setStartPoseText(nextText);
  }, []);

  useEffect(() => {
    if (status !== 'running') return;
    speakText(overlayText);
  }, [status, overlayText, speakText]);

  useEffect(() => {
    if (onStatusChange) onStatusChange(status);
  }, [status, onStatusChange]);

  // Keep latest angle callback available to the long-running detect loop.
  useEffect(() => {
    onAngleUpdateRef.current = onAngleUpdate;
  }, [onAngleUpdate]);

  // Initialise MediaPipe PoseLandmarker
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
        );
        const landmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task',
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          numPoses: 1,
          outputSegmentationMasks: false,
          // Temporal smoothing — reduces per-frame landmark jitter
          minPoseDetectionConfidence: 0.65,
          minPosePresenceConfidence: 0.65,
          minTrackingConfidence: 0.7,
        });

        if (cancelled) { landmarker.close(); return; }
        landmarkerRef.current = landmarker;

        // Optional face model for neck-rotation pose+face fusion.
        try {
          const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath:
                'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
              delegate: 'GPU',
            },
            runningMode: 'VIDEO',
            numFaces: 1,
            outputFaceBlendshapes: false,
            outputFacialTransformationMatrixes: false,
            minFaceDetectionConfidence: 0.6,
            minFacePresenceConfidence: 0.6,
            minTrackingConfidence: 0.65,
          });
          if (!cancelled) {
            faceLandmarkerRef.current = faceLandmarker;
          } else {
            faceLandmarker.close();
          }
        } catch (faceErr) {
          console.warn('[CVTest] Face model init skipped (pose-only fallback):', faceErr);
          faceLandmarkerRef.current = null;
        }

        // Optional hand model for pose-check tests.
        const calcType = cvTestConfig?.landmarkConfig?.calculation_type || '';
        if (calcType.endsWith('_pose_check')) {
          try {
            const handLandmarker = await HandLandmarker.createFromOptions(vision, {
              baseOptions: {
                modelAssetPath:
                  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
                delegate: 'GPU',
              },
              runningMode: 'VIDEO',
              numHands: 2,
              minHandDetectionConfidence: 0.55,
              minHandPresenceConfidence: 0.55,
              minTrackingConfidence: 0.5,
            });
            if (!cancelled) {
              handLandmarkerRef.current = handLandmarker;
            } else {
              handLandmarker.close();
            }
          } catch (handErr) {
            console.warn('[CVTest] Hand model init skipped (pose-only fallback):', handErr);
            handLandmarkerRef.current = null;
          }
        }

        setStatus('ready');
      } catch (err) {
        console.error('[CVTest] MediaPipe init error:', err);
        if (!cancelled) {
          setErrorMsg('Failed to load pose model. Please check your connection and try again.');
          setStatus('error');
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [cvTestConfig]);

  // Start camera and tracking
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startTest = useCallback(async () => {
    if (!landmarkerRef.current || !cvTestConfig) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 560 }, height: { ideal: 420 } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();

      // Init tracker
      trackerRef.current = new MovementTracker(cvTestConfig, side);
      resetSpurlingsState();
      overlayBaselineRef.current = null;
      startPoseHoldStartRef.current = null;
      startPoseLockedRef.current = false;
      startPoseLockedAtRef.current = null;
      sagittalSideLockedRef.current = null;
      sagittalSideCandidateRef.current = null;
      sagittalSideCandidateSinceRef.current = null;
      promptLastTextRef.current = '';
      promptLastSetAtRef.current = 0;
      setStartPoseText('');
      setStartPoseLocked(false);
      setStartPoseCorrect(false);
      setMovementComplete(false);
      movementCompleteRef.current = false;
      setStatus('running');

      // Start detection loop
      const detect = () => {
        if (!landmarkerRef.current || !trackerRef.current) return;

        const now = performance.now();
        const detections = landmarkerRef.current.detectForVideo(video, now);

        if (detections.landmarks && detections.landmarks.length > 0) {
          const landmarks = detections.landmarks[0];
          let faceDetections = null;
          if (faceLandmarkerRef.current) {
            try {
              faceDetections = faceLandmarkerRef.current.detectForVideo(video, now);
            } catch (_) {
              faceDetections = null;
            }
          }
          const faceLandmarks = faceDetections?.faceLandmarks?.[0] || null;
          const worldLandmarks = detections.worldLandmarks?.[0] || null;

          // ---- Pose-check branch ----
          // Skip normal angle tracking entirely; just evaluate end pose each frame.
          const calcTypeRaw = cvTestConfig?.landmarkConfig?.calculation_type || '';
          if (calcTypeRaw.endsWith('_pose_check')) {
            let handDetections = null;
            if (handLandmarkerRef.current) {
              try {
                handDetections = handLandmarkerRef.current.detectForVideo(video, now);
              } catch (_) {
                handDetections = null;
              }
            }
            const handLandmarksArray = handDetections?.landmarks || null;
            let poseResult;
            if (calcTypeRaw === 'vasalva_pose_check') {
              poseResult = evaluateVasalvaPose(landmarks, handLandmarksArray);
            } else if (calcTypeRaw === 'bakody_pose_check') {
              poseResult = evaluateBakodyPose(landmarks, handLandmarksArray);
            } else if (calcTypeRaw === 'ulntt_medianus_pose_check') {
              poseResult = evaluateUlnttMedianusPose(landmarks, handLandmarksArray);
            } else if (calcTypeRaw === 'ulntt_radialis_pose_check') {
              poseResult = evaluateUlnttRadialisPose(landmarks, handLandmarksArray);
            } else if (calcTypeRaw === 'ulntt_ulnaris_pose_check') {
              poseResult = evaluateUlnttUlnarisPose(landmarks, handLandmarksArray);
            } else if (calcTypeRaw === 'spurlings_pose_check') {
              poseResult = evaluateSpurlingsPose(landmarks);
            } else if (calcTypeRaw === 'neck_retraction_pose_check') {
              poseResult = evaluateNeckRetractionPose(landmarks);
            } else if (calcTypeRaw === 'neck_retraction_extension_pose_check') {
              poseResult = evaluateNeckRetractionExtensionPose(landmarks);
            } else if (calcTypeRaw === 'neck_rep_extension_pose_check') {
              poseResult = evaluateRepExtensionPose(landmarks);
            } else if (calcTypeRaw === 'neck_rep_flexion_pose_check') {
              poseResult = evaluateRepFlexionPose(landmarks);
            } else if (calcTypeRaw === 'shoulder_shrug_rotation_pose_check') {
              poseResult = evaluateShoulderShrugRotationPose(landmarks);
            } else if (calcTypeRaw === 'mwm_neck_rotation_pose_check') {
              poseResult = evaluateMWMNeckRotationPose(landmarks, handLandmarksArray);
            } else {
              // Generic fallback
              poseResult = { correct: false, activeArm: null, checks: {} };
            }

            setCurrentPhase(poseResult.phase || '');

            // Report to parent via onAngleUpdate (reuses the callback channel)
            const angleUpdateCb = onAngleUpdateRef.current;
            if (angleUpdateCb) {
              angleUpdateCb({ poseCheck: poseResult });
            }

            drawLandmarks(canvasRef.current, video, landmarks, detections.segmentationMasks?.[0] || null);
            rafIdRef.current = requestAnimationFrame(detect);
            return;
          }

          const frameResult = trackerRef.current.processFrame(landmarks, now, { faceLandmarks });
          const promptMinAngle = Number(cvTestConfig?.passCriteria?.min_angle_reached) || 20;
          const calcTypeNow = calcTypeRaw;
          const isSagittalNow =
            calcTypeNow === 'neck_flexion_side'
            || calcTypeNow === 'neck_extension'
            || calcTypeNow === 'neck_sagittal_side'
            || identifier.toLowerCase().includes('neckflexionrangeofmovement')
            || identifier.toLowerCase().includes('neckextentionrangeofmovement');

          // Start-position gate: lock a neutral baseline only after posture is correct
          // and stable for a short hold.
          if (startPoseRequired && !startPoseLockedRef.current) {
            // For sagittal tests, lock the side only after sustained sideways posture
            // so side detection does not flip while the patient is turning.
            if (isSagittalNow) {
              const orientation = getPatientOrientation(landmarks);
              const sideways = orientation === 'sideways';
              const front = orientation === 'front';
              sidewaysScoreRef.current += sideways ? 2 : (front ? -2 : -2);
              sidewaysScoreRef.current = Math.max(-12, Math.min(12, sidewaysScoreRef.current));
              if (sidewaysScoreRef.current >= 10 && orientationForSagittal !== 'sideways') {
                setOrientationForSagittal('sideways');
              } else if (sidewaysScoreRef.current <= 0 && orientationForSagittal !== 'front') {
                setOrientationForSagittal('front');
              }

              if (!sagittalSideLockedRef.current && sidewaysScoreRef.current >= 10) {
                const candidate = detectSagittalSideFromShoulders(landmarks, worldLandmarks);
                if (candidate) {
                  if (sagittalSideCandidateRef.current !== candidate) {
                    sagittalSideCandidateRef.current = candidate;
                    sagittalSideCandidateSinceRef.current = now;
                  } else if ((sagittalSideCandidateSinceRef.current != null)
                    && (now - sagittalSideCandidateSinceRef.current >= 900)) {
                    sagittalSideLockedRef.current = candidate;
                    setStableStartPoseText('Side profile locked. Sit tall and hold still for baseline.', { force: true });
                  }
                }
              }
            }

            const pose = evaluateStartPose({
              identifier,
              testName: cvTestConfig?.testName,
              calcType: calcTypeNow,
              landmarks,
              worldLandmarks,
            });
            const sagittalNeedsSideLock = isSagittalNow && !sagittalSideLockedRef.current;
            const poseReady = Boolean(pose.correct) && !sagittalNeedsSideLock;
            const poseText = sagittalNeedsSideLock
              ? 'Turn fully sideways and hold still to lock side detection.'
              : (pose.text || 'Hold still in this start position.');
            setStableStartPoseText(poseText, { minHoldMs: 2200 });
            setStartPoseCorrect(poseReady);
            setCurrentPhase('neutral');

            if (poseReady) {
              if (startPoseHoldStartRef.current == null) {
                startPoseHoldStartRef.current = now;
              }
              if (now - startPoseHoldStartRef.current >= 3000) {
                startPoseLockedRef.current = true;
                startPoseLockedAtRef.current = now;
                setStartPoseLocked(true);
                setStableStartPoseText('Starting position locked. Begin the test movement now.', { force: true });
                overlayBaselineRef.current = frameResult.angle;
                if (trackerRef.current) trackerRef.current.reset();
                startPoseHoldStartRef.current = null;
              }
            } else {
              startPoseHoldStartRef.current = null;
            }

            // Keep showing camera and guidance, but don't stream live angle yet.
            drawLandmarks(canvasRef.current, video, landmarks, detections.segmentationMasks?.[0] || null);
            rafIdRef.current = requestAnimationFrame(detect);
            return;
          }

          if (overlayBaselineRef.current === null) {
            overlayBaselineRef.current = frameResult.angle;
          }

          const adjustedAngle = frameResult.angle - (overlayBaselineRef.current || 0);
          const freeMeasureCompletionReached = freeMeasure
            && startPoseLockedRef.current
            && Math.abs(adjustedAngle) >= promptMinAngle;

          // Dynamic posture detection for sagittal neck tests: only prompt movement
          // after patient has turned sideways to camera.
          if (isSagittalNow) {
            // After baseline lock, keep side orientation stable for the whole attempt.
            if (orientationForSagittal !== 'sideways') setOrientationForSagittal('sideways');
          } else {
            sidewaysScoreRef.current = 0;
            sagittalSideLockedRef.current = null;
            sagittalSideCandidateRef.current = null;
            sagittalSideCandidateSinceRef.current = null;
            if (orientationForSagittal !== 'front') setOrientationForSagittal('front');
          }

          setCurrentPhase(frameResult.phase);
          if (frameResult.violations.length > 0) {
            setViolations(frameResult.violations);
            // In cv_guided mode, show real-time compensation warnings
            if (isGuided) {
              const latest = frameResult.violations[frameResult.violations.length - 1];
              setGuidanceText('⚠ ' + (latest.name || '').replace(/_/g, ' ') + ' — adjust your posture');
            }
          } else if (isGuided) {
            // Show encouraging guidance based on current phase
            const phaseText = phaseDisplayName(frameResult.phase);
            setGuidanceText(phaseText ? (phaseText + '… keep going') : 'Keep moving…');
          }

          // Report live angle to parent for real-time gauge update
          const angleUpdateCb = onAngleUpdateRef.current;
          if (angleUpdateCb && trackerRef.current) {
            // For side-view sagittal tests, also compute torso inclination.
            // calcType is neck_sagittal_side or neck_extension — both use the
            // same shoulder→hip geometry for trunk lean.
            const calcType = cvTestConfig?.landmarkConfig?.calculation_type || '';
            const isSagittal =
              calcType === 'neck_sagittal_side'
              || calcType === 'neck_flexion_side'
              || calcType === 'neck_extension';
            let torsoAngle = null;
            let sagittalAngles = null;
            let sagittalBaselineMetrics = null;
            let rotationBaselineMetrics = null;
            if (isSagittal) {
              try { torsoAngle = calculateTorsoInclinationSide(landmarks); } catch (_) {}
              try { sagittalAngles = calculateSagittalHeadTorsoAngles(landmarks); } catch (_) {}
              try {
                sagittalBaselineMetrics = computeSagittalBaselineMetrics(
                  landmarks,
                  worldLandmarks,
                  sagittalSideLockedRef.current,
                );
              } catch (_) {}
            }
            const rotationNeutralSimilarity = computeNoseEarSimilarity(landmarks);
            if (!isSagittal) {
              try { rotationBaselineMetrics = computeRotationBaselineMetrics(landmarks); } catch (_) {}
            }
            angleUpdateCb({
              // Parent measurement flow performs its own warmup/baseline calibration.
              // Keep this raw to avoid double baseline subtraction and low readings.
              angle: frameResult.angle,
              maxAngle: trackerRef.current.maxAngle,
              phase: frameResult.phase,
              violations: frameResult.violations,
              torsoInclination: torsoAngle,
              sagittalAngles,
              sagittalBaselineMetrics,
              rotationBaselineMetrics,
              rotationNeutralSimilarity,
            });
          }

          // Draw landmarks and segmentation mask on canvas
          const segMask = detections.segmentationMasks?.[0] || null;
          drawLandmarks(canvasRef.current, video, landmarks, segMask);

          const msSinceLock = startPoseLockedAtRef.current == null ? 0 : (now - startPoseLockedAtRef.current);
          const completionPromptReady = msSinceLock >= 1200;
          if ((frameResult.allPhasesComplete || freeMeasureCompletionReached) && !movementCompleteRef.current && completionPromptReady) {
            movementCompleteRef.current = true;
            setMovementComplete(true);
            setStableStartPoseText('✅ Good job. Try a few times, then return facing the camera.', { force: true });
            if (isGuided) setGuidanceText('✅ Good job. Try a few times, then return facing the camera.');
            speakText('Good job. Try a few times, then return facing the camera.');
          }

          if (frameResult.allPhasesComplete && !freeMeasure) {
            submitResults();
            return; // Stop loop
          }
        }

        rafIdRef.current = requestAnimationFrame(detect);
      };

      rafIdRef.current = requestAnimationFrame(detect);
    } catch (err) {
      console.error('[CVTest] Camera error:', err);
      setErrorMsg('Camera access denied. Please allow camera access and try again.');
      setStatus('error');
    }
  }, [cvTestConfig, side, isGuided, identifier, orientationForSagittal, startPoseRequired, startPoseLocked, speakText, setStableStartPoseText]);

  // Optional auto-start (used for dual-side embedded capture flow).
  useEffect(() => {
    if (!autoStartSignal) return;
    if (status === 'ready') {
      startTest();
    }
  }, [autoStartSignal, status, startTest]);

  // Submit sampled landmarks to backend
  const submitResults = useCallback(async () => {
    setStatus('submitting');
    // In embedded mode, keep the live feed visible while parent transitions
    // to the next question. Standalone mode can stop immediately.
    if (!embedded) {
      stopCamera();
    }

    const tracker = trackerRef.current;
    if (!tracker) return;

    const { landmarkFrames, localResult } = tracker.getResults();

    // Show local result immediately
    setResult(localResult);

    // Send to backend for authoritative validation || OBS should store in frontend in localstorage and then send to backend when user completes full assessment --> do we need auth for the free assessment?
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://api.virtus-one.com/question-handler/cv-test/validate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Token ${token}` } : {}),
        },
        body: JSON.stringify({
          assessment_id: data.assessmentId || '',
          question_id: data.id,
          test_definition_id: cvTestConfig.testDefinitionId,
          side,
          landmark_frames: landmarkFrames,
        }),
      });

      if (response.ok) {
        const serverResult = await response.json();
        // Update with server-authoritative result
        setResult({
          computedAngles: serverResult.computed_angles,
          phaseResults: serverResult.phase_results,
          maxAngleReached: serverResult.max_angle_reached,
          compensationViolations: serverResult.compensation_violations,
          testPassed: serverResult.test_passed,
          journalPhrase: serverResult.journal_phrase,
          metaJournalPhrase: serverResult.meta_journal_phrase,
        });
      }
      // Even if server returns error, local result is still shown
    } catch (err) {
      console.error('[CVTest] Submit error:', err);
      // Non-fatal: local result is still valid for display
    }

    setStatus('done');
  }, [data, cvTestConfig, side, embedded]);

  // Force submit (user clicks done early)
  const handleManualSubmit = useCallback(() => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    submitResults();
  }, [submitResults]);

  // Parent-driven submit trigger for embedded free-measure flows.
  useEffect(() => {
    if (!embedded || !freeMeasure) return;
    if (manualSubmitSignal > lastManualSubmitSignalRef.current) {
      lastManualSubmitSignalRef.current = manualSubmitSignal;
      pendingManualSubmitRef.current = true;
    }
    if (!pendingManualSubmitRef.current) return;
    if (status !== 'running') return;
    pendingManualSubmitRef.current = false;
    handleManualSubmit();
  }, [manualSubmitSignal, embedded, freeMeasure, status, handleManualSubmit]);

  // Continue to next question
  const handleContinue = useCallback(() => {
    if (!result) return;

    const tracker = trackerRef.current;
    const cvResultData = {
      maxAngleReached: result.maxAngleReached,
      testPassed: result.testPassed,
      compensationViolations: result.compensationViolations,
      side,
      landmarkFrames: tracker ? tracker.getSampledFrames() : [],
    };

    // In embedded/hybrid mode, report back to parent instead of calling selectedChoiceCallback directly
    if (embedded && onCvComplete) {
      // Auto-map angle to answer ID if mapping exists and mode is "cv_measure"
      let autoAnswerId = null;
      if (mappingMode === 'cv_measure' && answerMapping && result.maxAngleReached != null) {
        const angle = result.maxAngleReached;
        const match = answerMapping.find(m => angle >= m.min_angle && angle < m.max_angle);
        if (match) autoAnswerId = match.answer_id;
      }
      onCvComplete(cvResultData, autoAnswerId);
      return;
    }

    // Standalone mode — pass answer to questionnaire flow
    selectedChoiceCallback({
      id: 'cv_result',
      answer: JSON.stringify(cvResultData),
    });
  }, [result, selectedChoiceCallback, onCvComplete, embedded, side, answerMapping, mappingMode]);

  // In embedded cv_measure mode, auto-continue to parent when done
  // (parent renders its own measurement gauge, so skip the CV result panel)
  // handleContinue is held in a ref so this effect only fires on genuine status/result
  // transitions — not on every parent re-render that recreates the callback (which
  // would cause an infinite update loop when the parent updates live angle state).
  useEffect(() => { handleContinueRef.current = handleContinue; });
  useEffect(() => {
    if (status === 'done' && embedded && result) {
      handleContinueRef.current();
    }
  }, [status, embedded, result]);

  // Retry
  const handleRetry = useCallback(() => {
    stopCamera();
    if (trackerRef.current) trackerRef.current.reset();
    resetSpurlingsState();
    overlayBaselineRef.current = null;
    startPoseHoldStartRef.current = null;
    startPoseLockedRef.current = false;
    startPoseLockedAtRef.current = null;
    sagittalSideLockedRef.current = null;
    sagittalSideCandidateRef.current = null;
    sagittalSideCandidateSinceRef.current = null;
    promptLastTextRef.current = '';
    promptLastSetAtRef.current = 0;
    setResult(null);
    setViolations([]);
    setCurrentPhase('');
    setGuidanceText('');
    setStartPoseText('');
    setStartPoseLocked(false);
    setStartPoseCorrect(false);
    setMovementComplete(false);
    movementCompleteRef.current = false;
    setOrientationForSagittal('front');
    sidewaysScoreRef.current = 0;
    setStatus('ready');
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      stopCamera();
      if (landmarkerRef.current) {
        landmarkerRef.current.close();
        landmarkerRef.current = null;
      }
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close();
        faceLandmarkerRef.current = null;
      }
      if (handLandmarkerRef.current) {
        handLandmarkerRef.current.close();
        handLandmarkerRef.current = null;
      }
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  function stopCamera() {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  // ---- Rendering ----

  if (status === 'loading') {
    return (
      <div className={classes.root}>
        {embedded && autoStartSignal ? (
          <div style={{ width: '100%', maxWidth: 480, borderRadius: 8, background: '#e0e0e0', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
            <CircularProgress size={24} style={{ color: '#9e9e9e' }} />
          </div>
        ) : (
          <>
            <CircularProgress size={embedded ? 24 : 40} />
            {!embedded && <Typography style={{ marginTop: 16 }}>Loading pose detection model…</Typography>}
          </>
        )}
      </div>
    );
  }

  if (status === 'error') {
    // In embedded mode, errors are non-fatal — parent still shows answer options
    if (embedded) return null;
    return (
      <div className={classes.root}>
        <Typography color="error" style={{ marginBottom: 16 }}>{errorMsg}</Typography>
        <Button variant="outlined" onClick={() => window.location.reload()}>Reload</Button>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Title */}
      {!embedded && (
        <Typography variant="h6" gutterBottom style={{ textAlign: 'center' }}>
          {data.title || cvTestConfig?.testName || 'Movement Test'}
        </Typography>
      )}

      {/* Instructions (before start) — hidden when auto-starting */}
      {status === 'ready' && !autoStartSignal && (
        <div className={classes.instructionBox}>
          <Typography variant="body1" style={{ marginBottom: 16 }}>
            {instructions || `Position yourself so your full upper body is visible. Side: ${side}`}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={startTest}
          >
            Start Test
          </Button>
        </div>
      )}
      {/* Skeleton placeholder while waiting for auto-start */}
      {status === 'ready' && autoStartSignal && embedded && (
        <div style={{ width: '100%', maxWidth: 480, borderRadius: 8, background: '#e0e0e0', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '8px auto 0' }}>
          <CircularProgress size={24} style={{ color: '#9e9e9e' }} />
        </div>
      )}

      {/* Camera feed — always in DOM so videoRef is available for startTest */}
      <div className={classes.videoContainer} style={{
        display: (status === 'running' || status === 'submitting' || (embedded && status === 'done')) ? undefined : 'none',
        border: (status === 'running' && startPoseRequired && !startPoseLocked)
          ? `3px solid ${startPoseCorrect ? '#2e7d32' : '#616161'}`
          : undefined,
      }}>
        <video ref={videoRef} className={classes.video} playsInline muted />
        <canvas ref={canvasRef} className={classes.canvas} />
        {(status === 'running' || status === 'submitting') && (
          <div className={classes.overlay}>
            <div className={classes.phaseLabel} style={{ color: movementComplete ? '#81c784' : '#fff' }}>{overlayText}</div>
            {violations.length > 0 && (
              <div className={classes.compensationWarning}>
                ⚠ {violations.map((v) => v.name.replace(/_/g, ' ')).join(', ')}
              </div>
            )}
          </div>
        )}
        {(status === 'running' || status === 'submitting') && embeddedOverlayContent && (
          <div style={{
            position: 'absolute',
            left: '50%',
            bottom: 0,
            transform: 'translateX(-50%)',
            zIndex: 3,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            {embeddedOverlayContent}
          </div>
        )}
      </div>
      {(status === 'running' || status === 'submitting') && (
        <>
          {/* Guidance strip for cv_guided mode */}
          {isGuided && guidanceText && (
            <div style={{
              textAlign: 'center', padding: '8px 12px', marginTop: 4,
              backgroundColor: guidanceText.startsWith('⚠') ? '#fff3e0' : guidanceText.startsWith('✓') ? '#e8f5e9' : '#e3f2fd',
              borderRadius: 8, maxWidth: 480, margin: '4px auto',
            }}>
              <Typography variant="body2">{guidanceText}</Typography>
            </div>
          )}
          {status === 'submitting' && <LinearProgress style={{ width: '100%', maxWidth: 480 }} />}
          {status === 'running' && !embedded && (
            <div className={classes.btnRow}>
              <Button variant="outlined" size="small" onClick={handleManualSubmit}>
                Done
              </Button>
              <Button variant="outlined" size="small" color="secondary" onClick={handleRetry}>
                Retry
              </Button>
            </div>
          )}
        </>
      )}

      {/* Results — only in standalone mode (embedded mode parent handles display) */}
      {status === 'done' && result && !embedded && (
        <Paper className={classes.resultBox} elevation={2}>
          <Typography variant="h6" gutterBottom>
            {result.testPassed ? '✓ Test Complete' : 'Test Complete'}
          </Typography>
          <Typography variant="body1">
            Maximum angle: <strong>{result.maxAngleReached}°</strong>
          </Typography>
          {cvTestConfig?.normalRange && (
            <Typography variant="body2" color="textSecondary">
              Normal range: {cvTestConfig.normalRange.min_normal}°–{cvTestConfig.normalRange.max_normal}°
            </Typography>
          )}
          {result.compensationViolations && result.compensationViolations.length > 0 && (
            <Typography variant="body2" style={{ color: '#ff9800', marginTop: 8 }}>
              Compensations detected: {[...new Set(result.compensationViolations.map((v) => v.name))].join(', ').replace(/_/g, ' ')}
            </Typography>
          )}
          <Box mt={2}>
            {result.phaseResults && result.phaseResults.map((pr, i) => (
              <Typography key={i} variant="body2">
                {phaseDisplayName(pr.name)}: {pr.completed ? '✓' : '—'}
              </Typography>
            ))}
          </Box>
          <div className={classes.btnRow}>
            <Button variant="outlined" size="small" onClick={handleRetry}>
              Retry
            </Button>
            <Button variant="contained" color="primary" onClick={handleContinue}>
              Continue
            </Button>
          </div>
        </Paper>
      )}
    </div>
  );
}

// ---- Canvas drawing helpers ----

// Body-part colored connections (MediaPipe Pose standard)
const POSE_CONNECTIONS = [
  // Face (white)
  { indices: [[0, 1], [1, 2], [2, 3], [3, 7], [0, 4], [4, 5], [5, 6], [6, 8], [9, 10]], color: 'rgba(255,255,255,0.7)' },
  // Torso (green)
  { indices: [[11, 12], [11, 23], [12, 24], [23, 24]], color: 'rgba(0,200,100,0.8)' },
  // Left arm — user's left (cyan/blue)
  { indices: [[11, 13], [13, 15], [15, 17], [15, 19], [15, 21], [17, 19]], color: 'rgba(0,180,255,0.8)' },
  // Right arm (orange)
  { indices: [[12, 14], [14, 16], [16, 18], [16, 20], [16, 22], [18, 20]], color: 'rgba(255,140,0,0.8)' },
  // Left leg (cyan/blue)
  { indices: [[23, 25], [25, 27], [27, 29], [27, 31], [29, 31]], color: 'rgba(0,180,255,0.8)' },
  // Right leg (orange)
  { indices: [[24, 26], [26, 28], [28, 30], [28, 32], [30, 32]], color: 'rgba(255,140,0,0.8)' },
];

function drawLandmarks(canvas, video, landmarks, segmentationMask) {
  if (!canvas || !video) return;
  const ctx = canvas.getContext('2d');
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return;
  canvas.width = w;
  canvas.height = h;
  ctx.clearRect(0, 0, w, h);

  // ---- Segmentation mask overlay ----
  if (segmentationMask) {
    try {
      const maskData = segmentationMask.getAsFloat32Array();
      const imgData = ctx.createImageData(w, h);
      const pixels = imgData.data;
      for (let i = 0; i < maskData.length; i++) {
        const conf = maskData[i];
        if (conf > 0.3) {
          const idx = i * 4;
          pixels[idx] = 0;       // R
          pixels[idx + 1] = 45;  // G
          pixels[idx + 2] = 85;  // B (#002D55)
          pixels[idx + 3] = conf * 60; // semi-transparent
        }
      }
      ctx.putImageData(imgData, 0, 0);
    } catch (_) { /* mask not available — skip silently */ }
  }

  // ---- Connections with body-part colors ----
  ctx.lineWidth = 3;
  for (const group of POSE_CONNECTIONS) {
    ctx.strokeStyle = group.color;
    for (const [a, b] of group.indices) {
      if (a >= landmarks.length || b >= landmarks.length) continue;
      const la = landmarks[a];
      const lb = landmarks[b];
      if (la.visibility < 0.5 || lb.visibility < 0.5) continue;
      ctx.beginPath();
      ctx.moveTo(la.x * w, la.y * h);
      ctx.lineTo(lb.x * w, lb.y * h);
      ctx.stroke();
    }
  }

  // ---- Keypoints ----
  for (let i = 0; i < landmarks.length; i++) {
    const lm = landmarks[i];
    if (lm.visibility < 0.5) continue;
    ctx.fillStyle = i <= 10 ? '#ffffff' : (i % 2 === 0 ? '#FF8C00' : '#00B4FF');
    ctx.beginPath();
    ctx.arc(lm.x * w, lm.y * h, 5, 0, 2 * Math.PI);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}
